import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { startOfDay } from 'date-fns';
import React from 'react';
import { ArrowRight, Calendar, PlusSquare } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { enumToIcon, enumToStatusType } from '../../../types/common';
import { computeOverallRiskLikelihoodConsequence } from '../../../types/plan';
import { ProjectSummary } from '../../../types/project';
import { Badge, IconButton, StatusIcon } from '../../global';

const useTableColumnsClasses = makeStyles({
    equipment: {
        display: 'flex',
        flexDirection: 'column',
        '& > span:first-child': {
            color: theme.colors.neutral[1],
            ...theme.fonts.labelSemiBold,
        },
        '& > span:last-child': {
            color: theme.colors.neutral[2],
            ...theme.fonts.caption,
        },
    },
    type: {
        display: 'flex',
        gap: 8,
    },
    status: {
        textTransform: 'capitalize',
    },
    timeline: {
        display: 'flex',
        alignItems: 'center',
        color: theme.colors.neutral[1],
        ...theme.fonts.label,
        whiteSpace: 'nowrap',
        '&.past': {
            color: theme.colors.red[1],
        },
    },
    risk: {
        display: 'flex',
    },
    attributes: {
        display: 'flex',
        margin: '0 -5px',
        '& > *': {
            margin: '0 5px',
        },
    },
});

export const ColumnEquipment = (props: { project: ProjectSummary }) => {
    const classes = useTableColumnsClasses();
    return (
        <div className={classes.equipment}>
            <span>{props.project.name}</span>
            <span>{props.project.code}</span>
        </div>
    );
};

export const ColumnType = (props: { project: ProjectSummary }) => {
    const classes = useTableColumnsClasses();

    return (
        <div className={classes.type}>
            {(props.project.external_activity_required ||
                ['external', 'combined'].includes(props.project.inspection_type!)) && (
                <Badge type='neutral'>{'ext'}</Badge>
            )}
            {(props.project.internal_activity_required ||
                ['internal', 'combined'].includes(props.project.inspection_type!)) && (
                <Badge type='neutral'>{'int'}</Badge>
            )}
        </div>
    );
};

export const ColumnStatus = (props: { project: ProjectSummary }) => {
    const classes = useTableColumnsClasses();
    const { t } = usePrefixTranslation('common');
    const status = props.project.status ?? (props.project.plan != undefined ? 'planned' : 'not planned');
    return (
        <StatusIcon className={classes.status} type={enumToStatusType[status]} icon={enumToIcon[status]}>
            {t(`status.${status}`)}
        </StatusIcon>
    );
};

export const ColumnTimeline = (props: { project: ProjectSummary; type: 'internal' | 'external' }) => {
    const classes = useTableColumnsClasses();
    const { t, Trans } = usePrefixTranslation('project', { project: 'list.ProjectList' });
    const status = props.project.status ?? (props.project.plan != undefined ? 'planned' : 'not planned');
    const dashes = '- - - - - - - -';
    const dueRequired = props.project[`${props.type}_activity_required` as const];
    const due = props.project[`${props.type}_due_date` as const] ?? new Date(0);
    const dueString = dueRequired
        ? t(+due !== 0 ? 'table-column-inspection-date' : 'table-column-inspection-no-date', { date: due })
        : dashes;
    const start =
        props.project.status !== 'completed'
            ? props.project.estimated_start_date ?? new Date(0)
            : props.project.in_progress_date ?? new Date(0);
    const end =
        props.project.status !== 'completed'
            ? props.project.estimated_completed_date ?? new Date(0)
            : props.project.completed_date ?? new Date(0);
    const rangeRequired = ['combined', props.type].includes(props.project.inspection_type!);
    const rangeString = rangeRequired ? (
        <Trans
            i18nKey={'table-column-inspection-range'}
            values={{ start, end }}
            components={{ arrow: <ArrowRight style={{ width: 15, height: 15 }} /> }}
        />
    ) : (
        dashes
    );
    const cls = {
        past:
            (status === 'planned' && dueRequired && +due < +startOfDay(new Date())) ||
            (status === 'ready to inspect' && rangeRequired && +start < +startOfDay(new Date())),
    };

    return (
        <div className={clsx(classes.timeline, cls)}>
            {status === 'not planned' ? (
                <span>{'- - - - - - - -'}</span>
            ) : status === 'planned' ? (
                <span>{dueString}</span>
            ) : (
                <span>{rangeString}</span>
            )}
        </div>
    );
};

export const ColumnRisk = (props: { project: ProjectSummary }) => {
    const { t } = usePrefixTranslation('common');
    const risk =
        props.project.likelihood_risk != undefined && props.project.consequence_risk != undefined
            ? computeOverallRiskLikelihoodConsequence(props.project.likelihood_risk, props.project.consequence_risk)
            : 'not-defined';

    return <Badge type={enumToStatusType[risk]}>{t(`probability.${risk}`)}</Badge>;
};

export const ColumnAttributes = (props: { project: ProjectSummary }) => {
    const classes = useTableColumnsClasses();

    return (
        <div className={classes.attributes}>
            {props.project.rope_access && <StatusIcon type='neutral' icon={'R'} />}
            {props.project.internal_scaffold && <StatusIcon type='neutral' icon={'S'} />}
            {props.project.heavy_cleaning && <StatusIcon type='neutral' icon={'C'} />}
            <div style={{ flex: 1 }} />
            {props.project.inspection === -1 && (
                <IconButton size='small'>
                    <Calendar />
                </IconButton>
            )}
            {props.project.plan == undefined && (
                <IconButton size='small'>
                    <PlusSquare />
                </IconButton>
            )}
        </div>
    );
};
